/**
 * Agent access (ADR-009 §5.2): the dock panel and the two dialogs an agent
 * can never answer — the pairing Allow dialog and per-action consent.
 *
 * Every decision button acts only on a trusted click (`event.isTrusted`), so
 * neither page script nor an add-on can approve on the user's behalf, and an
 * agent has no path to this UI at all. The panel lists pairings (clients are
 * self-reported: "reports itself as …"), their permissions and folders, the
 * connection, the activity log, per-document full-preview grants, and the
 * Disconnect/Forget kill switches.
 */
import { useEffect, useRef, useState, useSyncExternalStore, type MouseEvent as ReactMouseEvent } from 'react';
import { SCOPE_DESCRIPTIONS } from '@maipo/agent-protocol';
import {
  agentState,
  allowPairing,
  answerConsent,
  connectPairing,
  consentClickTooSoon,
  denyPairing,
  disconnectBridge,
  loadPairings,
  revokePairing,
  setAutoConnect,
  setFullPreview,
  subscribeAgentState,
  updateAgentState,
  type AgentAccessState,
} from '../agent/agent-store';
import type { ConsentRequest } from '../agent/gateway';
import { useFocusTrap } from '../../shell/hooks';

function useAgentState(): AgentAccessState {
  return useSyncExternalStore(subscribeAgentState, agentState, agentState);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const EGRESS_LABEL: Record<string, string> = {
  none: '—',
  metadata: 'metadata',
  'bounded-preview': 'preview',
  'full-preview': 'full preview',
  'local-folder': 'folder',
  return: 'to agent',
  upload: 'upload',
};

function statusText(state: AgentAccessState): { label: string; tone: 'off' | 'busy' | 'on' | 'warn' } {
  switch (state.status.state) {
    case 'idle': return { label: 'Not connected', tone: 'off' };
    case 'connecting': return { label: state.status.pairing ? 'Pairing…' : 'Connecting…', tone: 'busy' };
    case 'connected': return { label: 'Connected', tone: 'on' };
    case 'disconnected': return { label: 'Disconnected', tone: 'off' };
    case 'error': return { label: 'Not connected', tone: 'warn' };
  }
}

export function AgentAccessPanel({ sessionId, documentName }: { sessionId: string; documentName: string }) {
  const state = useAgentState();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // Reading IndexedDB is local; it never touches the network.
    if (!state.loaded) void loadPairings().catch(() => updateAgentState({ loaded: true }));
  }, [state.loaded]);
  const status = statusText(state);
  const activeId = state.status.state === 'connected' || state.status.state === 'connecting' ? state.status.pairingId : undefined;
  const message = state.status.state === 'error' || state.status.state === 'disconnected' ? state.status.reason : null;
  const act = (run: () => Promise<void>) => {
    setBusy(true);
    void run().catch((error: unknown) => updateAgentState({ status: { state: 'error', reason: error instanceof Error ? error.message : String(error) } })).finally(() => setBusy(false));
  };
  return <section className="agent-panel" aria-labelledby="agent-panel-title">
    <header className="agent-panel-head">
      <div><strong id="agent-panel-title">Agent access</strong><span>Local only · nothing is uploaded</span></div>
      <span className={`agent-status agent-status-${status.tone}`} data-state={state.status.state}>{status.label}</span>
    </header>
    <p className="agent-live" aria-live="polite">{message ?? (state.status.state === 'connected' ? `Connected to the local helper. Agents act as “Agent · <client>” in History.` : '')}</p>

    {state.pairings.length === 0 ? <div className="agent-empty">
      <p>Let an AI agent on this computer, such as Claude Code, work on your open documents through the <code>maipo-mcp</code> helper. The agent talks to this tab over your own machine; documents are not uploaded anywhere.</p>
      <p>To pair, run <code>maipo-mcp pair</code> in a terminal. It opens a link here that asks for your permission.</p>
    </div> : <ul className="agent-pairings" aria-label="Paired helpers">
      {state.pairings.map((pairing) => {
        const connected = activeId === pairing.pairingId && state.status.state === 'connected';
        const connecting = activeId === pairing.pairingId && state.status.state === 'connecting';
        return <li key={pairing.pairingId} className="agent-pairing" data-pairing={pairing.pairingId}>
          <div className="agent-pairing-head">
            <div><strong>{pairing.grants.client}</strong><span>reports itself as this client · paired {pairing.createdAt.slice(0, 10)}</span></div>
            {connected
              ? <button type="button" onClick={() => act(disconnectBridge)}>Disconnect</button>
              : <button type="button" className="primary" disabled={busy || connecting} onClick={() => act(() => connectPairing(pairing.pairingId))}>{connecting ? 'Connecting…' : 'Connect'}</button>}
          </div>
          <ul className="agent-scopes" aria-label="Permissions">
            {pairing.grants.scopes.map((scope) => <li key={scope} title={scope}>{SCOPE_DESCRIPTIONS[scope]}</li>)}
          </ul>
          {pairing.grants.folders.length ? <div className="agent-folders"><span>Export folders</span>{pairing.grants.folders.map((folder) => <code key={folder}>{folder}</code>)}</div> : null}
          {pairing.grants.importFolders?.length ? <div className="agent-folders agent-import-folders"><span>Import folders (the agent can read image files here)</span>{pairing.grants.importFolders.map((folder) => <code key={folder}>{folder}</code>)}</div> : null}
          <div className="agent-pairing-actions">
            <label><input type="checkbox" checked={pairing.autoConnect} onChange={(event) => act(() => setAutoConnect(pairing.pairingId, event.target.checked))} /> Connect automatically when Maipo opens</label>
            <button type="button" className="agent-danger" onClick={() => act(() => revokePairing(pairing.pairingId))} aria-label={`Forget pairing with ${pairing.grants.client}`}>Forget</button>
          </div>
        </li>;
      })}
    </ul>}

    <fieldset className="agent-grants">
      <legend>This document</legend>
      <label><input type="checkbox" checked={state.fullPreviewSessions.has(sessionId)} onChange={(event) => setFullPreview(sessionId, event.target.checked)} /> Allow full-resolution previews of “{documentName}”</label>
      <label><input type="checkbox" checked={state.allowWithoutCheckpoint} onChange={(event) => updateAgentState({ allowWithoutCheckpoint: event.target.checked })} /> Allow agent changes even if a recovery checkpoint cannot be saved</label>
    </fieldset>

    <div className="agent-activity">
      <div className="agent-activity-head"><strong>Activity</strong><span>{state.activity.length ? `${state.activity.length} this session` : 'No agent activity yet'}</span>
        {state.status.state === 'connected' || state.status.state === 'connecting' ? <button type="button" className="agent-danger" onClick={() => act(disconnectBridge)}>Disconnect all</button> : null}
      </div>
      {state.activity.length ? <ol className="agent-log" aria-label="Agent activity, newest first">
        {state.activity.map((entry) => <li key={entry.id} data-status={entry.status} data-tool={entry.tool}>
          <time dateTime={entry.time}>{entry.time.slice(11, 19)}</time>
          <span className="agent-log-tool">{entry.tool}</span>
          <span className="agent-log-client">{entry.client}</span>
          <span className="agent-log-result">{entry.status === 'ok' ? 'ok' : entry.code}</span>
          <span className="agent-log-bytes">{formatBytes(entry.bytesOut)}</span>
          <span className="agent-log-egress">{EGRESS_LABEL[entry.egress] ?? entry.egress}</span>
          {entry.note ? <span className="agent-log-note">{entry.note}</span> : null}
        </li>)}
      </ol> : null}
    </div>
  </section>;
}

function useCountdown(expiresAt: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

/** When the dialog appeared; decision clicks within CONSENT_CLICK_DELAY_MS of it are ignored. */
function useShownAt(): number {
  const [shownAt] = useState(() => performance.now());
  return shownAt;
}

function ConsentDialog({ request }: { request: ConsentRequest }) {
  const ref = useRef<HTMLDivElement>(null);
  const seconds = useCountdown(request.expiresAt);
  const shownAt = useShownAt();
  const decide = (event: ReactMouseEvent, decision: 'allow' | 'deny', disconnect = false) => {
    if (consentClickTooSoon(shownAt, performance.now())) return;
    answerConsent(request.id, decision, event.nativeEvent.isTrusted, { disconnect });
  };
  // Escape or dismissal is a denial.
  useFocusTrap(true, ref, () => answerConsent(request.id, 'deny', true));
  const allowLabel = request.kind === 'publish' ? 'Upload once' : request.kind === 'export-return' ? 'Send once' : request.kind === 'close-unsaved' ? 'Close without saving' : 'Delete layers';
  return <div className="modal-backdrop agent-consent-backdrop" role="presentation">
    <div ref={ref} className="dialog agent-dialog" role="alertdialog" aria-modal="true" aria-labelledby={`${request.id}-title`} aria-describedby={`${request.id}-detail`} data-consent={request.kind}>
      <div className="dialog-heading"><div><span className="eyebrow">Agent request · {request.client} (self-reported)</span><h2 id={`${request.id}-title`}>{request.title}</h2></div></div>
      <p className="dialog-copy" id={`${request.id}-detail`}>{request.documentName ? <>Document “{request.documentName}”. </> : null}{request.detail}</p>
      <p className="agent-dialog-note">Only you can answer this; the agent cannot. It is declined automatically in {seconds} s.</p>
      <div className="dialog-actions">
        <button type="button" autoFocus onClick={(event) => decide(event, 'deny')}>Deny</button>
        <button type="button" onClick={(event) => decide(event, 'deny', true)}>Deny and disconnect</button>
        <button type="button" className="primary" onClick={(event) => decide(event, 'allow')}>{allowLabel}</button>
      </div>
    </div>
  </div>;
}

function PairingDialog({ state }: { state: AgentAccessState }) {
  const ref = useRef<HTMLDivElement>(null);
  const link = state.pendingLink;
  const shownAt = useShownAt();
  useFocusTrap(Boolean(link), ref, denyPairing);
  if (!link) return null;
  return <div className="modal-backdrop agent-consent-backdrop" role="presentation">
    <div ref={ref} className="dialog agent-dialog agent-pair-dialog" role="alertdialog" aria-modal="true" aria-labelledby="agent-pair-title" aria-describedby="agent-pair-detail">
      <div className="dialog-heading"><div><span className="eyebrow">Agent access · pairing</span><h2 id="agent-pair-title">Allow “{link.grants.client}” to use Maipo?</h2></div></div>
      <p className="dialog-copy" id="agent-pair-detail">A helper on this computer asked to connect this tab to an AI agent. The name above is what the helper reports about itself. If you did not just run <code>maipo-mcp pair</code>, choose Deny.</p>
      <ul className="agent-scopes agent-scopes-dialog" aria-label="It asks to">
        {link.grants.scopes.map((scope) => <li key={scope}>{SCOPE_DESCRIPTIONS[scope]}</li>)}
      </ul>
      {link.grants.folders.length ? <div className="agent-folders"><span>Export folders: the agent can write full-resolution copies of open documents here</span>{link.grants.folders.map((folder) => <code key={folder}>{folder}</code>)}</div> : <p className="agent-dialog-note">No export folders.</p>}
      {link.grants.importFolders?.length ? <div className="agent-folders agent-import-folders"><span>Import folders: the agent can read image files (PNG, JPEG, WebP) in these folders and their subfolders and place them in your documents</span>{link.grants.importFolders.map((folder) => <code key={folder}>{folder}</code>)}</div> : <p className="agent-dialog-note">No import folders: the helper will not read any file for the agent.</p>}
      <p className="agent-dialog-note">Maipo connects only to the helper on this computer (port 47651). Uploads and anything larger than a small preview still ask you each time. Your browser may also ask whether this site may access your local network.</p>
      <div className="dialog-actions">
        <button type="button" autoFocus onClick={(event) => { if (event.nativeEvent.isTrusted) denyPairing(); }}>Deny</button>
        <button type="button" className="primary" onClick={(event) => {
          if (!event.nativeEvent.isTrusted || consentClickTooSoon(shownAt, performance.now())) return;
          void allowPairing(link).catch((error: unknown) => updateAgentState({ status: { state: 'error', reason: error instanceof Error ? error.message : String(error) } }));
        }}>Allow</button>
      </div>
    </div>
  </div>;
}

/** Rendered once at the workspace root, so requests show whichever panel is open. */
export function AgentDialogs() {
  const state = useAgentState();
  const consent = state.consents[0];
  return <>
    {/* Keyed by link, so the click delay restarts whenever a new request appears. */}
    <PairingDialog key={state.pendingLink?.pairingId ?? 'none'} state={state} />
    {!state.pendingLink && consent ? <ConsentDialog key={consent.id} request={consent} /> : null}
    {state.linkError ? <div className="toast agent-link-error">{state.linkError}<button type="button" onClick={() => updateAgentState({ linkError: null })} aria-label="Dismiss">×</button></div> : null}
  </>;
}
